/**
 * TurnController - Centralized Turn Management
 *
 * Phase 4 - SOC Refactoring
 *
 * Single source of truth for turn transitions and validation.
 * Consolidates turn-change logic that was previously split between ZodiacEngine
 * and GameStateManager, with special handling for bonus turns, tutorials, and different modes.
 *
 * RESPONSIBILITIES:
 * - Determine when turns should change
 * - Handle bonus turn skip logic
 * - Coordinate mode-specific turn behavior (Sandbox, Tutorial, TestAI, Live)
 * - Finalize turn (FEN history, event notifications)
 * - Clear status effects at turn boundaries
 */

import { GameState, Mode, State, IPiece } from '../types';
import { GameStateManager } from './GameStateManager';
import { BonusTurnManager } from './BonusTurnManager';
import { FENHistoryManager } from './FENHistoryManager';
import { TutorialEngineAdapter } from '../TutorialEngineAdapter';

export class TurnController {
  private gameStateManager: GameStateManager;
  private bonusTurnManager: BonusTurnManager;
  private fenHistoryManager: FENHistoryManager;

  constructor(
    gameStateManager: GameStateManager,
    bonusTurnManager: BonusTurnManager,
    fenHistoryManager: FENHistoryManager
  ) {
    this.gameStateManager = gameStateManager;
    this.bonusTurnManager = bonusTurnManager;
    this.fenHistoryManager = fenHistoryManager;
  }

  /**
   * Main turn change orchestrator
   * Handles all mode-specific logic and delegates to appropriate handlers
   *
   * @param finalizeCallback Callback to finalize FEN history (from MovementManager)
   * @param notifyCallback Callback to notify listeners (from EventManager)
   * @param remoteCallback Callback to send updates to remote (from EventManager)
   */
  changeTurns(
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): void {
    const gameState = this.gameStateManager.getGameState();


    // BONUS TURN: Skip turn change but finalize state
    if (this.bonusTurnManager.hasBonusTurn()) {
      this.handleBonusTurn(gameState, finalizeCallback, notifyCallback, remoteCallback);
      return;
    }

    // SANDBOX MODE: Skip turn changes entirely
    if (gameState.mode === Mode.Sandbox) {
      this.handleSandboxMode(gameState, finalizeCallback, notifyCallback, remoteCallback);
      return;
    }

    // TESTAI MODE: Check if this is tutorial or regular TestAI
    if (gameState.mode === Mode.TestAI) {
      const tutorialConfig = TutorialEngineAdapter.getTutorialConfig(gameState);

      if (!tutorialConfig) {
        // ChallengeAI uses TestAI mode WITHOUT tutorialConfig - handle normally
        // Fall through to regular turn change
      } else {
        // Tutorial mode using TestAI - delegate to adapter
        const handled = this.handleTutorialMode(
          gameState,
          finalizeCallback,
          notifyCallback,
          remoteCallback
        );
        if (handled) return;
      }
    }

    // TUTORIAL MODE: Delegate to adapter
    if (gameState.mode === Mode.Tutorial) {
      const handled = this.handleTutorialMode(
        gameState,
        finalizeCallback,
        notifyCallback,
        remoteCallback
      );
      if (handled) return;
    }

    // REGULAR TURN CHANGE
    this.executeRegularTurnChange(gameState, finalizeCallback, notifyCallback, remoteCallback);
  }

  /**
   * Handle bonus turn logic
   * Don't change turns, but finalize state and notify
   */
  private handleBonusTurn(
    gameState: GameState,
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): void {

    // CRITICAL FIX: Finalize FEN history for sandbox/dev mode even during bonus turns
    // This ensures virus/monkey/penguin bonus turn moves appear in move history
    if (gameState.mode === Mode.Sandbox || gameState.mode === Mode.Dev) {
      finalizeCallback();
    }

    // Still notify listeners that game state changed, but don't change turns
    // CRITICAL FIX: Do NOT send update to Firebase during bonus turn notification
    // The position was already sent by makeMove() with moveMade=true
    // Sending again with moveMade=false would overwrite with stale fenHistory
    notifyCallback();
    if (gameState.mode !== Mode.Live) {
      // Only send for non-Live modes (Sandbox/Dev need the update for UI)
      remoteCallback(false);
    } else {
    }
  }

  /**
   * Handle sandbox mode turn logic
   * Allow free movement of both colors without turn changes
   */
  private handleSandboxMode(
    gameState: GameState,
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): void {

    // Increment turn count so timed effects can expire
    gameState.turnCount++;

    // Clear timed status effects (poison, zombification, etc.)
    const allPieces = this.gameStateManager.getAllPieces();
    this.clearTimedStatusEffects(allPieces);

    // Don't change turns, just notify listeners and finalize FEN
    finalizeCallback();
    notifyCallback();
    remoteCallback();
  }

  /**
   * Handle tutorial mode turn logic
   * Delegates to TutorialEngineAdapter
   * @returns true if turn change was handled (blocked), false if should continue
   */
  private handleTutorialMode(
    gameState: GameState,
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): boolean {
    const turnResult = TutorialEngineAdapter.handleTutorialTurnChange(
      gameState,
      finalizeCallback,
      notifyCallback,
      remoteCallback
    );

    return turnResult.shouldBlockAction;
  }

  /**
   * Execute a regular turn change
   * Changes active player, increments turn count, finalizes state
   * CRITICAL: Must finalize FEN BEFORE changing turns to detect threefold repetition correctly
   */
  private executeRegularTurnChange(
    gameState: GameState,
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): void {
    const fromPlayer = gameState.isLightTurn ? 'light' : 'dark'; // Player who just moved
    const turnChangeStartTime = Date.now();

    // CRITICAL: Finalize FEN history FIRST (before changing turns)
    // This allows us to check for threefold repetition with the complete history
    finalizeCallback();

    // Check for threefold repetition AFTER FEN is added but BEFORE turn changes
    // This ensures the draw is detected with correct turn state
    if (this.fenHistoryManager.hasThreefoldRepetition(gameState)) {
      gameState.state = State.Draw;
      (gameState as any).drawReason = "3 time repeat draw";

      // Notify listeners and send remote update, but don't change turns
      notifyCallback();
      remoteCallback();
      return;
    }

    // Change the active player
    this.gameStateManager.changeTurns();

    const toPlayer = gameState.isLightTurn ? 'light' : 'dark'; // Player whose turn it now is

    // Clear timed status effects (poison, zombification, etc.)
    const allPieces = this.gameStateManager.getAllPieces();
    this.clearTimedStatusEffects(allPieces);

    // Notify listeners and send remote update
    notifyCallback();
    remoteCallback();
  }

  /**
   * Finalize turn by updating FEN history and notifying listeners
   */
  finalizeTurn(
    finalizeCallback: () => void,
    notifyCallback: () => void,
    remoteCallback: (moveMade?: boolean) => void
  ): void {

    // Finalize FEN history
    finalizeCallback();

    // Notify all listeners
    notifyCallback();

    // Send update to remote if applicable
    remoteCallback();

  }

  /**
   * Force end the current turn without a move being made.
   * Used for zombie ink trap when player selects a shadow.
   * This bypasses the normal move flow and just changes turns.
   */
  forceEndTurn(): void {
    const gameState = this.gameStateManager.getGameState();
    const fromPlayer = gameState.isLightTurn ? 'light' : 'dark';

    // Change the active player
    this.gameStateManager.changeTurns();

    const toPlayer = gameState.isLightTurn ? 'light' : 'dark';

    // Clear timed status effects (poison, zombification, etc.)
    const allPieces = this.gameStateManager.getAllPieces();
    this.clearTimedStatusEffects(allPieces);
  }

  /**
   * Check if a turn change should occur after a move
   * Used by MovementManager to determine if changeTurns should be called
   */
  shouldChangeTurn(): boolean {
    const gameState = this.gameStateManager.getGameState();

    // Don't change turns during bonus turns
    if (this.bonusTurnManager.hasBonusTurn()) {
      return false;
    }

    // Don't change turns in sandbox mode
    if (gameState.mode === Mode.Sandbox) {
      return false;
    }

    // Normal turn change
    return true;
  }

  /**
   * Clear all timed status effects for the current player
   * Called at the start of a player's turn
   */
  clearTimedStatusEffects(allPieces: IPiece[]): void {
    const gameState = this.gameStateManager.getGameState();
    const isInBonusTurn = this.bonusTurnManager.hasBonusTurn();

    allPieces.forEach((piece) => {
      // DEBUG: Log poison check
      if (piece.poisonedStatus.isPoisoned) {
      }

      // CRITICAL: Don't remove poison during bonus turns!
      // Poison should only be removed when the turn FULLY ends
      // Use <= to handle cases where turn count skips values
      if (
        !isInBonusTurn &&
        piece.poisonedStatus.isPoisoned &&
        piece.poisonedStatus.removeOnTurn !== undefined &&
        piece.poisonedStatus.removeOnTurn <= gameState.turnCount
      ) {
        piece.poisonedStatus = {
          isPoisoned: false,
          removeOnTurn: undefined
        };
      }

      // CRITICAL: Don't remove zombification during bonus turns!
      // Zombification should only be removed when the turn FULLY ends
      // Use <= (same as poison) so zombie clears after the controlling player's turn ends
      // At this point turnCount was already incremented, so the controlling player has already moved
      if (piece.zombifiedStatus.isZombified) {
      }
      if (
        !isInBonusTurn &&
        piece.zombifiedStatus.isZombified &&
        piece.zombifiedStatus.removeOnTurn !== undefined &&
        piece.zombifiedStatus.removeOnTurn <= gameState.turnCount
      ) {
        piece.zombifiedStatus = {
          isZombified: false,
          removeOnTurn: undefined
        };
        piece.isZombified = false;
      }
    });
  }
}
